import styles from "components/sections/search/SearchHome.module.scss";
import Link from "next/link";
import CloseIcon from "images/icons/close.svg";
import {chunkedArray, getTimeFromTimestamp, shuffle, swiperCustomAnimation} from "common/utils";
import {Swiper, SwiperSlide} from "swiper/react";
import {useContext, useEffect, useMemo, useState} from "react";
import HorizontalProducts from "components/layout/store/products/HorizontalProducts";
import {KEY_PROMOTION_ENDS_AT} from "pages/store/special-offers";
import {Axios} from "api";
import {productScrapContextRef} from "context/ProductScrapContext";
import 'swiper/css';
import "swiper/css/pagination";
import "swiper/css/navigation";
import {
    brandRoute,
    eventsRoute,
    getStoreProductListRoute,
    paletteRoute,
    sellerShopRoute,
    specialOffersRoute,
    storeRoute
} from "common/const";
import TruncateMarkup from "react-truncate-markup";
import UserContext from "context/AuthContext";
import {SearchQueryLocalStorageKey} from "components/sections/SearchSection";
import {captureException, captureMessage} from "@sentry/nextjs";
import {KEY_SELL_COUNT} from "pages/store/products";
import useTranslation from "next-translate/useTranslation";


const SearchShortcut = (props) => {
    const {title, link, onClick} = props;

    return (
        <Link href={link}>
            <a className={styles.shortcut} onClick={onClick}>
                <span className={styles.shortcutTitle}>{title}</span>
            </a>
        </Link>
    )
};

const RecentSearchQuery = (props) => {
    const {query, onDelete} = props;
    const onClick = props.onClick || (() => {
    });

    const onDeleteButtonClick = (e) => {
        e.stopPropagation();
        onDelete();
    }

    return (
        <div className={styles.recentSearchQueryWrapper} onClick={onClick}>
            <span className={styles.recentSearchQuery}>{query}</span>
            <CloseIcon viewBox="0 0 20 20" className={styles.recentSearchQueryClose} onClick={onDeleteButtonClick}/>
        </div>
    )
};

const PopularSearchQuery = (props) => {
    const {query, rank} = props;
    const onClick = props.onClick || (() => {
    });

    return (
        <div className={styles.popularSearchQueryWrapper} onClick={onClick}>
            <span className={styles.popularSearchQueryRank}>{rank}</span>
            <TruncateMarkup lines={1} ellipsis={<span>...</span>}>
                <span className={styles.popularSearchQuery}>{query}</span>
            </TruncateMarkup>
        </div>
    )
};

const MAX_RECENT_SEARCH_QUERIES = 10;

const SearchHomeShortcutInfo = [
    {
        title: '⏰ 마감임박',
        link: () => `${specialOffersRoute}`,
        key: 'timeOffers'
    },
    {
        title: '🥇 랭킹',
        link: () => getStoreProductListRoute(KEY_SELL_COUNT),
        key: 'ranking'
    },
    {
        title: '❤️ 인기 팔레트',
        link: () => `${paletteRoute}`,
        key: 'popularPalette'
    },
    {
        title: '🌟 신규브랜드',
        link: () => `${brandRoute}`,
        key: 'newBrand'
    },
    {
        title: '🤩 이벤트',
        link: () => `${eventsRoute}`,
        key: 'event'
    },
];

const SearchHome = (props) => {
    const {setSearchText, onItemClick} = props;

    const [products, setProducts] = useState([]);

    const [recentQueries, setRecentQueries] = useState([]);
    const [popularQueryInfo, setPopularQueryInfo] = useState({
        queries: [],
        updated_at: null,
    });
    const user = useContext(UserContext).user;

    useEffect(() => {
        fetchSearchStats();
        fetchPopularProducts();
    }, []);

    const fetchSearchStats = async () => {
        try {
            const res = await Axios.get('v1/search/stats');
            if (res.status < 400) {
                if (user) {
                    setRecentQueries(res.data.recent_queries.slice(0, MAX_RECENT_SEARCH_QUERIES));
                } else {
                    const queries = JSON.parse(window.localStorage.getItem(SearchQueryLocalStorageKey)) || [];
                    setRecentQueries(queries);
                }
                setPopularQueryInfo(res.data.popular_query_info);
            } else {
                captureMessage(JSON.stringify(res.data));
            }
        } catch (e) {
            captureException(e);
        } finally {
        }
    };

    const deleteRecentQuery = async (query) => {
        try {
            if (user) {
                const res = await Axios.delete(`v1/search/stats`, {params: {query}});
                if (res.status < 400) {
                    console.log('search stats data', res.data)
                    setRecentQueries(res.data.recent_queries.slice(0, MAX_RECENT_SEARCH_QUERIES));
                } else {
                    captureMessage(JSON.stringify(res.data));
                }
            } else {
                const queries = recentQueries.filter(x => x !== query);
                window.localStorage.setItem(SearchQueryLocalStorageKey, JSON.stringify(queries));
                setRecentQueries(queries);
            }
        } catch (e) {
            captureException(e);
        } finally {
        }
    };

    const deleteAllRecentQueries = () => {
        deleteRecentQuery(null);
        window.localStorage.removeItem(SearchQueryLocalStorageKey);
        setRecentQueries([]);
    };

    const handleRecentSearchQueryClick = (query) => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            "SearchSectionRecentSearchQueryClick",
            {query}
        );
        setSearchText(query);
    };

    const fetchPopularProducts = async () => {
        try {
            const params = {category_id: 0, order: KEY_PROMOTION_ENDS_AT};
            const res = await Axios.get('v1/curation/search_home_product_recommendation/', {params: params});
            if (res.status < 400) {
                const products = shuffle(res.data.item_list).slice(0, 10);
                setProducts(products);
                if (res.data.products && res.data.products.length > 0) {
                    productScrapContextRef.current.updateByProductIdsAndScrappedProductIds(
                        res.data.products.map(e => e.id), res.data['scrapped_pids'],
                    );
                }
            } else {
                captureMessage(JSON.stringify(res.data));
            }
        } catch (e) {
            captureException(e);
        } finally {
        }
    };

    const chunkedPopularQueries = useMemo(() => {
        return chunkedArray(popularQueryInfo.queries.map(x => x.query), 6);
    }, [popularQueryInfo]);

    const popularQueryUpdatedTime = useMemo(() => {
        if (!popularQueryInfo.updated_at) {
            return null;
        }
        return getTimeFromTimestamp(popularQueryInfo.updated_at);
    }, [popularQueryInfo]);

    const onPopularQueriesSlideChange = (swiper) => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'SearchSectionPopularQuerySwiperSwipe',
            {
                activeIndex: swiper.activeIndex,
            }
        );
    };

    const onShortcutClick = (e) => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'SearchSectionShortcutClick',
            {
                title: e,
            }
        );
        onItemClick();
    };
    const {t} = useTranslation('components-sections-search-SearchHome');
    return (
        <div className={styles.content}>
            <div className={styles.shortcutContainer}>
                {
                    SearchHomeShortcutInfo.map(({title, link, key}) => {
                        return <SearchShortcut key={`SearchShortcut-${title}`} title={t(`shortcut.${key}`)} link={link()} onClick={() => onShortcutClick(title)}/>
                    })
                }
            </div>
            {
                recentQueries && recentQueries.length > 0 &&
                <div className={styles.sectionContainer}>
                    <div className={styles.sectionTitleWrapper}>
                        <span className={styles.sectionTitle}>{t('recentKeyword')}</span>
                        <span className={styles.sectionTitleDelete} onClick={deleteAllRecentQueries}>{t('deleteAll')}</span>
                    </div>
                    <div className={styles.recentSearchQueryContainer}>
                        {
                            recentQueries.map((query, i) => (
                                <RecentSearchQuery
                                    key={`RecentSearchQuery-${i}`} query={query}
                                    onClick={() => handleRecentSearchQueryClick(query)}
                                    onDelete={() => deleteRecentQuery(query)}
                                />
                            ))
                        }
                    </div>
                </div>
            }
            {
                popularQueryInfo.queries.length > 0 &&
                <div className={styles.sectionContainer}>
                    <div className={styles.sectionTitleWrapper}>
                        <span className={styles.sectionTitle}>{t('popularKeyword')}</span>
                        <span className={styles.sectionTitleDelete}>{t('popularQueryTimeAs', {time: popularQueryUpdatedTime})}</span>
                    </div>
                    <Swiper
                        onSetTranslate={swiperCustomAnimation}
                        spaceBetween={0}
                        slidesPerView={1}
                        threshold={30}
                        pagination={{
                            clickable: true,
                            horizontalClass: styles.productImageSwiperHorizontalClass,
                            bulletClass: styles.productImageSwiperBulletClass,
                            bulletActiveClass: styles.productImageSwiperBulletActiveClass,
                        }}
                        onSlideChange={onPopularQueriesSlideChange}
                    >
                        {
                            chunkedPopularQueries.map((queryGroup, i) => {
                                return (
                                    <SwiperSlide key={`popular-query-slide-${i}`}>
                                        <div className={styles.popularSearchQueryGroup}>
                                            {
                                                queryGroup.map((query, j) => (
                                                    <PopularSearchQuery
                                                        key={`PopularSearchQuery-${i * 6 + j}`}
                                                        rank={i * 6 + j + 1}
                                                        query={query}
                                                        onClick={() => setSearchText(query)}
                                                    />
                                                ))
                                            }
                                        </div>
                                    </SwiperSlide>
                                )
                            })
                        }
                    </Swiper>
                </div>
            }
            {
                products && products.length > 0 &&
                <div className={styles.sectionContainer}>
                    <div className={styles.sectionTitleWrapper}>
                        <span className={styles.sectionTitle}>{t('hotProduct')}</span>
                        <Link href={storeRoute}>
                            <a className={styles.sectionTitleShowMore}>
                                <span className={styles.sectionTitleShowMoreText}>{t('more')}</span>
                                {/*<ArrowRightIcon viewBox="0 0 16 16" className={styles.sectionTitleShowMoreIcon}/>*/}
                            </a>
                        </Link>
                    </div>
                    <div style={{height: 16}}/>
                    <div className={styles.productListContainer}>
                        <HorizontalProducts products={products} miniScrapIcon/>
                    </div>
                </div>
            }
            <div className={styles.bottomPadding} />
        </div>
    )
};

export default SearchHome;
